import { useMemo, useRef } from 'react';

import type { GraphMessage } from '@/api/types/inbox';
import { useContextEmails } from '@/features/inbox/hooks/useContextEmails';
import { useInboxInfinite } from '@/features/inbox/hooks/useInboxInfinite';
import { transformGraphMessageToEmailItem } from '@/utils/emailTransform';
import { useIntersectionObserver } from '@/utils/hooks/useIntersectionObserver';
import { useUserLocaleAndTimezone } from '@/utils/hooks/useUserLocaleAndTimezone';

interface UseEmailDataParams {
  contextEmailIds?: Array<string> | undefined;
  debouncedSearch: string;
  open: boolean;
  selectedEmails: Set<string>;
  sessionId?: string | null;
}

export function useEmailData({
  contextEmailIds,
  debouncedSearch,
  open,
  selectedEmails,
  sessionId,
}: UseEmailDataParams) {
  const { locale, timeZone } = useUserLocaleAndTimezone();
  const {
    data: inboxInfiniteData,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInboxInfinite({
    enabled: open,
    ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
    contextEmailIds,
    sessionId,
  });

  const {
    data: contextEmailsData,
    error: _contextEmailsError,
    isLoading: _isContextEmailsLoading,
  } = useContextEmails({
    contextEmailIds,
    enabled: open && !!contextEmailIds?.length,
  });

  const allSelectedEmailIds = useMemo(() => {
    const ids = new Set<string>();
    contextEmailIds?.forEach((id) => ids.add(id));
    selectedEmails.forEach((id) => ids.add(id));
    return Array.from(ids);
  }, [contextEmailIds, selectedEmails]);

  const {
    data: selectedEmailsData,
    error: _selectedEmailsError,
    isLoading: _isSelectedEmailsLoading,
  } = useContextEmails({
    contextEmailIds: allSelectedEmailIds,
    enabled: open && allSelectedEmailIds.length > 0,
  });

  const emails = useMemo(() => {
    const allValues = inboxInfiniteData?.pages.flatMap((p) => p.value ?? []) ?? [];
    return allValues
      .map(
        (item): GraphMessage => ({
          ...(item.bodyPreview != null && { bodyPreview: item.bodyPreview }),
          ...(item.from && { from: { address: item.from.email, name: item.from.name } }),
          id: item.id,
          ...(item.isRead != null && { isRead: item.isRead }),
          receivedDateTime: item.receivedDateTime ?? '',
          sender: item.from
            ? { emailAddress: { address: item.from.email, name: item.from.name } }
            : { emailAddress: { address: '', name: '' } },
          subject: item.subject ?? '',
        }),
      )
      .map((msg) => transformGraphMessageToEmailItem(msg, debouncedSearch, locale, timeZone));
  }, [inboxInfiniteData?.pages, debouncedSearch, locale, timeZone]);

  const inboxScrollRef = useRef<HTMLDivElement>(null);
  const { ref: inboxSentinelRef } = useIntersectionObserver(
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    {
      enabled: open && hasNextPage === true && !isFetchingNextPage,
      root: inboxScrollRef,
      rootMargin: '100px',
      threshold: 0.1,
    },
  );

  const contextEmailItems = useMemo(() => {
    if (!contextEmailsData) {
      return [];
    }
    return contextEmailsData.map((msg) =>
      transformGraphMessageToEmailItem(msg, undefined, locale, timeZone),
    );
  }, [contextEmailsData, locale, timeZone]);

  const selectedEmailItemsData = useMemo(() => {
    if (!selectedEmailsData) {
      return [];
    }
    return selectedEmailsData.map((msg) =>
      transformGraphMessageToEmailItem(msg, undefined, locale, timeZone),
    );
  }, [selectedEmailsData, locale, timeZone]);

  const combinedEmailMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof transformGraphMessageToEmailItem>>();
    emails.forEach((email) => {
      map.set(email.id, email);
    });
    contextEmailItems.forEach((email) => {
      map.set(email.id, email);
    });
    selectedEmailItemsData.forEach((email) => {
      map.set(email.id, email);
    });
    return map;
  }, [emails, contextEmailItems, selectedEmailItemsData]);

  const selectedEmailItems = useMemo(() => {
    const result: Array<ReturnType<typeof transformGraphMessageToEmailItem>> = [];
    selectedEmails.forEach((id) => {
      const email = combinedEmailMap.get(id);
      if (email) {
        result.push(email);
      }
    });
    return result;
  }, [combinedEmailMap, selectedEmails]);

  return {
    emails,
    error: error ?? null,
    hasNextPage: hasNextPage ?? false,
    inboxScrollRef,
    inboxSentinelRef,
    isFetchingNextPage,
    isLoading,
    selectedEmailItems,
  };
}
